import axios from "axios";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { userStateAtom } from "../atoms/userStateAtom";
import CustomedAvatar from "./CustomedAvatar";

interface userRanking {
  user_id: string;
  point: string;
}

interface StudyResultInterface {
  no: string;
  type: string;
  use_time: string;
  count: string;
  occupants: string;
  start: string;
  reg_date: string;
  rankingList: userRanking[];
}

const AllRoomResultRecord = () => {
  const [userLoginState, setUserLoginState] = useRecoilState(userStateAtom);
  const userId = userLoginState.userData.userIdphp;
  const [userRankingData, setUserRankingData] = useState<
    StudyResultInterface[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage] = useState(10);

  function removeTimeFromDate(input: string | null): string {
    if (!input) {
      return "-";
    }
    return input.split(" ")[0];
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://chamstudyland.com/api/fun_learning/getRankingByRoom.php"
        );
        setUserRankingData(response.data);
      } catch {
        setError("데이터를 가져오는데 실패했습니다.");
      }
    };
    fetchData();
  }, []);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = userRankingData.slice(indexOfFirstItem, indexOfLastItem);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(userRankingData.length / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  const [currentPageGroup, setCurrentPageGroup] = useState(0); // 추가된 상태: 현재 페이지 그룹
  const pagesPerGroup = 5; // 한 그룹에 표시할 페이지 수

  const handlePageGroupChange = (direction: "next" | "prev") => {
    if (
      direction === "next" &&
      (currentPageGroup + 1) * pagesPerGroup < pageNumbers.length
    ) {
      setCurrentPageGroup(currentPageGroup + 1);
    } else if (direction === "prev" && currentPageGroup > 0) {
      setCurrentPageGroup(currentPageGroup - 1);
    }
  };

  const startIndex = currentPageGroup * pagesPerGroup;
  const endIndex = startIndex + pagesPerGroup;
  const visiblePages = pageNumbers.slice(startIndex, endIndex); // 현재 그룹에 해당하는 페이지 번호만 표시

  return (
    <div>
      <div className="dark:text-slate-200 font-bold text-slate-800 text-xl max-w-sm mx-auto my-5">
        방별학습기록보기
      </div>

      <table className="max-w-xs mx-auto bg-white dark:bg-gray-900 rounded-lg overflow-hidden shadow-lg mb-6">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th
              scope="col"
              className="px-3 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              번호
            </th>
            <th
              scope="col"
              className="px-3 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              시작시간
            </th>
            <th
              scope="col"
              className="px-3 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              걸린시간
            </th>
            <th
              scope="col"
              className="px-3 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              랭킹리스트
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900">
          {currentItems.map((user, index) => (
            <tr key={index}>
              <td className="px-6 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900 dark:text-gray-200">
                  {user.no}
                </div>
              </td>
              <td className="px-3 py-1 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {removeTimeFromDate(user.start)}
                </div>
              </td>
              <td className="px-3 py-1 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {user.use_time}
                </div>
              </td>
              <td className="px-3 py-1 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                  {user.rankingList.map((userRecord) => (
                    <div>{`${userRecord.user_id} ${userRecord.point}`}</div>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-center mb-6">
        <nav className="relative z-0 inline-flex shadow-sm px-5 bg-slate-100 dark:bg-gray-800 rounded-lg">
          {/* 이전 그룹으로 이동 */}
          <button
            onClick={() => handlePageGroupChange("prev")}
            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
              currentPageGroup > 0
                ? "bg-blue-500 text-slate-300 border-none"
                : "bg-gray-300 text-gray-500 cursor-not-allowed border-none dark:bg-gray-600 dark:text-slate-300"
            }`}
            disabled={currentPageGroup === 0}
          >
            {"<"}
          </button>
          {/* 현재 그룹의 페이지 번호들 */}
          {visiblePages.map((number) => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                currentPage === number
                  ? "bg-blue-500 dark:bg-blue-800 text-slate-300 border-none"
                  : "bg-slate-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-none"
              }`}
            >
              {number}
            </button>
          ))}
          {/* 다음 그룹으로 이동 */}
          <button
            onClick={() => handlePageGroupChange("next")}
            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
              (currentPageGroup + 1) * pagesPerGroup < pageNumbers.length
                ? "bg-blue-500 text-slate-300 border-none"
                : "bg-gray-300 text-gray-500 cursor-not-allowed border-none dark:bg-gray-600 dark:text-slate-300"
            }`}
            disabled={
              (currentPageGroup + 1) * pagesPerGroup >= pageNumbers.length
            }
          >
            {">"}
          </button>
        </nav>
      </div>
    </div>
  );
};

export default AllRoomResultRecord;
